import React, { useEffect, useRef, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, Space, Switch, Select, Tag, Radio,
  Row, Col, Drawer, message, Tooltip,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import request from '../utils/request';
import { fmtMoney, fmtTime } from '../utils/format';
import ImageUpload from '../components/ImageUpload';
import SkuImagesEditor from '../components/SkuImagesEditor';
import RichTextEditor from '../components/RichTextEditor';
import { ensureRichTextSafe } from '../utils/richtext';

// 抽屉内的分区白色卡片容器样式
const sectionStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #eef0f2',
  borderRadius: 10,
  padding: '18px 20px 2px',
  marginBottom: 16,
};

// 分区标题：左侧色条 + 标题 + 说明
const SectionTitle: React.FC<{ title: string; desc?: string }> = ({ title, desc }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: '#065F46' }}>
      <span style={{ width: 3, height: 15, background: '#065F46', borderRadius: 2, display: 'inline-block' }} />
      {title}
    </div>
    {desc && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 5, marginLeft: 11, lineHeight: 1.6 }}>{desc}</div>}
  </div>
);

// 规格表表头文字样式
const skuTh: React.CSSProperties = { fontSize: 13, color: '#6b7280', display: 'inline-block', marginBottom: 6 };

const Products: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [categories, setCategories] = useState<{ label: string; value: number }[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [kwInput, setKwInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [status, setStatus] = useState<number | undefined>();
  const [distribution, setDistribution] = useState<string | undefined>();
  const [points, setPoints] = useState<string | undefined>();
  const [form] = Form.useForm();
  // 打开抽屉时的商品类别，用于切换类别二次确认取消后回退
  const initialKindRef = useRef<'MALL' | 'POINTS'>('MALL');

  const fetchCategories = async () => {
    const res: any = await request.get('/category');
    setCategories((res || []).map((c: any) => ({ label: c.name, value: c.id })));
  };

  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const res: any = await request.get('/product', {
        params: {
          page: p, pageSize,
          keyword: keyword || undefined,
          categoryId,
          status,
          distribution,
          points,
        },
      });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page, keyword, categoryId, pageSize, status, distribution, points]);
  useEffect(() => { fetchCategories(); }, []);

  const hasFilters = !!(keyword || categoryId || status !== undefined || distribution || points);
  const resetFilters = () => {
    setKwInput(''); setKeyword(''); setCategoryId(undefined); setPage(1);
    setStatus(undefined); setDistribution(undefined); setPoints(undefined);
  };

  const openDrawer = (record?: any) => {
    form.resetFields();
    const kind: 'MALL' | 'POINTS' = record ? (record.productKind || 'MALL') : 'MALL';
    initialKindRef.current = kind;
    if (record) {
      form.setFieldsValue({
        ...record,
        // 图文详情直接是富文本 HTML，原样回填即可（不再做 Markdown 往返）
        // 商品图集：无 images 但有老单图 image 时，以 image 作为首张，避免保存时清空
        images: record.images
          ? String(record.images).split('\n').map((s: string) => s.trim()).filter(Boolean)
          : record.image
            ? [String(record.image).trim()].filter(Boolean)
            : [],
        limitPerUser: record.limitPerUser ?? undefined,
        pointsRate: record.pointsRate != null ? Number(record.pointsRate) : undefined,
        pointsPrice: record.pointsPrice ?? undefined,
        pointsExpireDays: record.pointsExpireDays ?? undefined,
        redeemType: record.redeemType || 'ONLINE',
        productKind: kind,
        distributionEnabled: record.distributionEnabled ?? false,
        commissionType: record.commissionType || 'RATE',
        commissionRate: record.commissionRate != null ? Number(record.commissionRate) : undefined,
        commissionAmount: record.commissionAmount != null ? Number(record.commissionAmount) : undefined,
        skus: (record.skus || []).map((s: any) => ({
          id: s.id,
          spec: s.spec,
          price: Number(s.price),
          originalPrice: s.originalPrice ? Number(s.originalPrice) : undefined,
          stock: s.stock,
          // 规格图集：无 images 但有老单图 image 时以 image 兜底，避免保存时清空
          images: s.images || (s.image ? String(s.image) : ''),
        })),
      });
    } else {
      form.setFieldsValue({ sort: 0, salesBase: 0, images: [], redeemType: 'ONLINE', productKind: 'MALL', distributionEnabled: false, commissionType: 'RATE', skus: [{ stock: 0, images: '' }] });
    }
    setEditItem(record || null);
    setDrawerOpen(true);
  };

  // 切换类别的联动清理：积分商品清分销配置并强制关分销，商城商品清积分配置并保证有规格行
  const applyKindCleanup = (kind: 'MALL' | 'POINTS') => {
    if (kind === 'POINTS') {
      form.setFieldsValue({ distributionEnabled: false, commissionRate: null, commissionAmount: null });
    } else {
      form.setFieldsValue({ pointsPrice: null, pointsExpireDays: null });
      if (!form.getFieldValue('skus')?.length) {
        form.setFieldsValue({ skus: [{ stock: 0, images: '' }] });
      }
    }
  };

  const handleKindChange = (next: 'MALL' | 'POINTS') => {
    const prev = initialKindRef.current;
    if (next === prev) return;
    if (!editItem) {
      applyKindCleanup(next);
      return;
    }
    Modal.confirm({
      title: '确认切换商品类别？',
      content: next === 'POINTS'
        ? '切换为积分商品将清除该商品的规格与分销佣金配置，且积分商品不参与分销。'
        : '切换为商城商品将清除该商品的积分兑换配置（兑换积分、过期天数），且必须至少配置一个规格。',
      okText: '确认切换',
      cancelText: '取消',
      onOk: () => { applyKindCleanup(next); },
      onCancel: () => { form.setFieldsValue({ productKind: prev }); },
    });
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditItem(null);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    // 图文详情：富文本直接存 HTML，仅补齐小程序 rich-text 需要的内联样式（img 自适应、表格边框等）
    if (values.detail) {
      values.detail = ensureRichTextSafe(values.detail);
    }
    // 商品图片：数组转换行字符串存储；第一张派生为主图（供列表/购物车/订单等缩略图）
    if (Array.isArray(values.images)) {
      values.image = values.images[0] || '';
      values.images = values.images.join('\n');
    }
    // 规格图集：images 规整为换行字符串，image 派生为第一张封面（供列表/购物车/订单缩略图）
    if (Array.isArray(values.skus)) {
      values.skus = values.skus.map((s: any) => {
        const imgs = (typeof s.images === 'string' ? s.images.split('\n') : Array.isArray(s.images) ? s.images : [])
          .map((x: string) => String(x).trim()).filter(Boolean);
        return { ...s, images: imgs.join('\n'), image: imgs[0] || '' };
      });
    }
    // 商品类别是唯一权威：POINTS 只带积分配置（强制退出分销、无规格），MALL 清空积分字段
    const kind: 'MALL' | 'POINTS' = values.productKind || 'MALL';
    if (kind === 'POINTS') {
      values.distributionEnabled = false;
      values.commissionRate = null;
      values.commissionAmount = null;
      values.skus = [];
      values.pointsPrice = values.pointsPrice ?? null;
      values.pointsExpireDays = values.pointsExpireDays ?? null;
    } else {
      values.pointsPrice = null;
      values.pointsExpireDays = null;
      // 佣金字段：未开启分销时清空，开启时按模式保留对应值
      if (!values.distributionEnabled) {
        values.commissionRate = null;
        values.commissionAmount = null;
      } else if (values.commissionType === 'RATE') {
        values.commissionAmount = null;
      } else {
        values.commissionRate = null;
      }
    }
    setSubmitting(true);
    try {
      if (editItem) {
        await request.put(`/product/${editItem.id}`, values);
        message.success('更新成功');
      } else {
        await request.post('/product', values);
        message.success('创建成功');
      }
      closeDrawer();
      fetchList();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除？',
      content: '将同时删除该商品的所有规格，并清理用户购物车中的对应条目',
      okButtonProps: { danger: true },
      onOk: async () => {
        await request.delete(`/product/${id}`);
        message.success('删除成功');
        fetchList();
      },
    });
  };

  // 上下架：缺货/错价可临时下架，无需删除商品毁历史
  const toggleStatus = (record: any) => {
    const next = record.status === 1 ? 0 : 1;
    Modal.confirm({
      title: next === 1 ? '确认上架该商品？' : '确认下架该商品？',
      content: next === 1
        ? `上架后「${record.name}」将在小程序商城展示售卖`
        : `下架后「${record.name}」立即从小程序商城隐藏，已有订单不受影响`,
      onOk: async () => {
        await request.put(`/product/${record.id}`, { status: next });
        message.success(next === 1 ? '已上架' : '已下架');
        fetchList();
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '商品名称', dataIndex: 'name', width: 200,
      ellipsis: { showTitle: false },
      render: (v: string) => <Tooltip title={v} placement="topLeft">{v || '-'}</Tooltip>,
    },
    {
      title: '分类', dataIndex: 'category', width: 100,
      render: (c: any) => c?.name || '-',
    },
    {
      title: '类别', width: 80,
      render: (_: any, r: any) => (r.productKind === 'POINTS'
        ? <Tag color="purple">积分</Tag>
        : <Tag color="green">商城</Tag>),
    },
    {
      title: '标签', width: 130,
      render: (_: any, r: any) => (
        <Space size={2}>
          {r.recommend && <Tag color="gold">精选</Tag>}
          {r.isNew && <Tag color="blue">新品</Tag>}
          {r.isHot && <Tag color="red">热卖</Tag>}
        </Space>
      ),
    },
    {
      title: '销量', dataIndex: 'sales', width: 80,
      render: (_: any, r: any) => (r.salesBase || 0) + (r.sales || 0),
    },
    {
      title: '规格', dataIndex: 'skus', width: 180,
      render: (skus: any[], r: any) => {
        if (r.productKind === 'POINTS') return <Tag>积分商品无需规格</Tag>;
        return skus?.length
          ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              {skus.map((s) => {
                const text = `${s.spec} ${fmtMoney(s.price)}`;
                return (
                  <Tag
                    key={s.id}
                    color={s.status === 1 ? 'green' : 'default'}
                    title={text}
                    style={{ margin: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {text}
                    {s.originalPrice && Number(s.originalPrice) > Number(s.price) && (
                      <del style={{ marginLeft: 4, opacity: 0.7 }}>{fmtMoney(s.originalPrice)}</del>
                    )}
                  </Tag>
                );
              })}
            </div>
          )
          : <Tag color="red">无规格</Tag>;
      },
    },
    {
      title: '分销', width: 120,
      render: (_: any, r: any) => {
        if (!r.distributionEnabled) return <Tag>未开启</Tag>;
        if (r.commissionType === 'FIXED') return <Tag color="orange">{fmtMoney(r.commissionAmount || 0)}/件</Tag>;
        return <Tag color="orange">{(Number(r.commissionRate || 0) * 100).toFixed(1)}%</Tag>;
      },
    },
    {
      title: '积分兑换', width: 150,
      render: (_: any, r: any) => (r.productKind === 'POINTS' && r.pointsPrice)
        ? (
          <Space size={2}>
            <Tag color="purple">{r.pointsPrice} 积分</Tag>
            <Tag color={r.redeemType === 'OFFLINE' ? 'cyan' : 'geekblue'}>
              {r.redeemType === 'OFFLINE' ? '线下' : '线上'}
            </Tag>
          </Space>
        )
        : '-',
    },
    { title: '排序', dataIndex: 'sort', width: 70 },
    {
      // 列表默认按更新时间倒序，刚编辑/新建的商品置顶
      title: '更新时间', dataIndex: 'updatedAt', width: 115,
      render: (v: string) => fmtTime(v),
    },
    {
      title: '创建时间', dataIndex: 'createdAt', width: 115,
      render: (v: string) => fmtTime(v),
    },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (v: number) => v === 1 ? <Tag color="green">上架</Tag> : <Tag>下架</Tag>,
    },
    {
      title: '操作', width: 190, fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" type="link" onClick={() => toggleStatus(record)}>
            {record.status === 1 ? '下架' : '上架'}
          </Button>
          <Button size="small" type="link" onClick={() => openDrawer(record)}>编辑</Button>
          <Button size="small" type="link" danger onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>商品管理</h2>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="搜索商品名称"
            style={{ width: 220 }}
            value={kwInput}
            onChange={(e) => setKwInput(e.target.value)}
            onSearch={(v) => { setPage(1); setKeyword(v.trim()); }}
          />
          <Select
            allowClear
            placeholder="分类筛选"
            style={{ width: 150 }}
            value={categoryId}
            onChange={(v) => { setPage(1); setCategoryId(v); }}
            options={categories}
          />
          <Select
            allowClear
            placeholder="上架状态"
            style={{ width: 110 }}
            value={status}
            onChange={(v) => { setPage(1); setStatus(v); }}
            options={[
              { label: '已上架', value: 1 },
              { label: '已下架', value: 0 },
            ]}
          />
          <Select
            allowClear
            placeholder="分销"
            style={{ width: 120 }}
            value={distribution}
            onChange={(v) => { setPage(1); setDistribution(v); }}
            options={[
              { label: '参与分销', value: '1' },
              { label: '未参与分销', value: '0' },
            ]}
          />
          <Select
            allowClear
            placeholder="类别"
            style={{ width: 120 }}
            value={points}
            onChange={(v) => { setPage(1); setPoints(v); }}
            options={[
              { label: '积分商品', value: '1' },
              { label: '商城商品', value: '0' },
            ]}
          />
          {hasFilters && <Button onClick={resetFilters}>重置</Button>}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer()}>
            新增商品
          </Button>
        </Space>
      </div>
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading} scroll={{ x: 1620 }}
        pagination={{
          current: page, total, pageSize, showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (p, ps) => { setPage(p); setPageSize(ps); }, showTotal: (t) => `共 ${t} 件商品`,
        }} />

      <Drawer
        title={editItem ? '编辑商品' : '新增商品'}
        open={drawerOpen}
        width={960}
        onClose={closeDrawer}
        forceRender
        styles={{ body: { background: '#f7f8fa', padding: 16 } }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={closeDrawer}>取消</Button>
            <Button type="primary" loading={submitting} onClick={handleSubmit}>保存</Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          {/* 商品类别 */}
          <div style={sectionStyle}>
            <SectionTitle title="商品类别" desc="先选类别再配置：商城商品走现金下单需规格；积分商品仅可用积分兑换，无需规格且不参与分销" />
            <Form.Item name="productKind" label="商品类别" initialValue="MALL"
              rules={[{ required: true, message: '请选择商品类别' }]}
              tooltip="决定该商品售卖方式与可配置项，编辑时切换类别会清除另一类的配置">
              <Radio.Group
                options={[
                  { label: '商城商品（现金购买）', value: 'MALL' },
                  { label: '积分商品（积分兑换）', value: 'POINTS' },
                ]}
                optionType="button"
                buttonStyle="solid"
                onChange={(e) => handleKindChange(e.target.value)}
              />
            </Form.Item>
          </div>

          {/* 基础信息 */}
          <div style={sectionStyle}>
            <SectionTitle title="基础信息" desc="商品名称、分类与图文描述，是列表和详情页展示的核心内容" />
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item name="name" label="商品名称" rules={[{ required: true, message: '请输入商品名称' }]}>
                  <Input placeholder="请输入商品名称" maxLength={100} showCount />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="categoryId" label="分类" tooltip="在「分类管理」中维护">
                  <Select options={categories} placeholder="选择分类" allowClear />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="description" label="简介" tooltip="列表卡片与详情页顶部展示">
              <Input.TextArea rows={2} placeholder="一句话卖点，如：道地药材，独立小包装" maxLength={200} showCount />
            </Form.Item>
            <Form.Item name="detail" label="图文详情"
              tooltip="所见即所得编辑，支持标题/加粗/列表/表格/图片；保存后在小程序商品详情页「图文详情」板块展示">
              <RichTextEditor height={400} />
            </Form.Item>
          </div>

          {/* 商品图片：合并为一组图集，第一张即主图 */}
          <div style={sectionStyle}>
            <SectionTitle title="商品图片" desc="第一张为主图（列表/搜索/购物车缩略图），全部图片在详情页顶部轮播；建议 1:1（如 800×800），非正方形会被裁切" />
            <Form.Item name="images" label="商品图片">
              <ImageUpload multiple tip="可上传多张，第一张自动作为主图" />
            </Form.Item>
          </div>

          {/* 药品信息 */}
          <div style={sectionStyle}>
            <SectionTitle title="药品信息" desc="药品资质与用法说明，展示在详情页参数区" />
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="approvalNo" label="批准文号">
                  <Input placeholder="如：国药准字H12345678" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="manufacturer" label="生产厂家">
                  <Input placeholder="请输入生产厂家" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="expiry" label="有效期 / 保质期">
                  <Input placeholder="如：24个月" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="usageInfo" label="用法用量">
                  <Input placeholder="如：口服，一次1片，一日3次" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* 销售设置 */}
          <div style={sectionStyle}>
            <SectionTitle title="销售设置" desc="销量、限购、积分倍率与首页展示标签" />
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="salesBase" label="销量基数" tooltip="展示销量 = 基数 + 真实销量，真实销量下单自动累加">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="limitPerUser" label="每人限购" tooltip="留空表示不限购">
                  <InputNumber min={1} placeholder="不限" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="pointsRate" label="积分倍率" tooltip="留空=1倍（1元1分），填0=不送积分">
                  <InputNumber min={0} max={9.9} step={0.5} placeholder="默认1倍" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="sort" label="排序" initialValue={0} tooltip="数值越大越靠前">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="recommend" label="首页精选" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isNew" label="新品标" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isHot" label="热卖标" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* 积分兑换：仅 POINTS 类别可见 */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.productKind !== cur.productKind}>
            {({ getFieldValue }) => getFieldValue('productKind') === 'POINTS' ? (
              <div style={sectionStyle}>
                <SectionTitle title="积分兑换" desc="该商品上架到积分商城，仅可用积分兑换、不在现金商城售卖，无需配置规格" />
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="pointsPrice" label="兑换所需积分" tooltip="小程序积分商城用积分兑换的价格"
                      rules={[{ required: true, message: '请输入兑换所需积分' }]}>
                      <InputNumber min={1} precision={0} placeholder="如 500" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="redeemType" label="兑换路径" tooltip="线上=快递发货到收货地址；线下=到店出示兑换码核销">
                      <Select
                        options={[
                          { label: '线上（快递发货）', value: 'ONLINE' },
                          { label: '线下（到店核销）', value: 'OFFLINE' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="pointsExpireDays" label="积分过期时间（天）" tooltip="兑换后超过该天数未完成则过期，积分自动退还；留空=永不过期">
                      <InputNumber min={1} precision={0} placeholder="永不过期" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            ) : null}
          </Form.Item>

          {/* 分销佣金：仅 MALL 类别可见（积分商品不产生现金订单，无佣金来源） */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.productKind !== cur.productKind}>
            {({ getFieldValue }) => getFieldValue('productKind') === 'MALL' ? (
              <div style={sectionStyle}>
                <SectionTitle title="分销佣金" desc="开启后，授权分销员分享此商品，好友下单完成即结算佣金" />
                <Form.Item name="distributionEnabled" label="参与分销" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.distributionEnabled !== cur.distributionEnabled}>
                  {({ getFieldValue: gfv }) => gfv('distributionEnabled') ? (
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name="commissionType" label="佣金方式">
                          <Radio.Group
                            options={[
                              { label: '按比例', value: 'RATE' },
                              { label: '固定金额', value: 'FIXED' },
                            ]}
                            optionType="button"
                            buttonStyle="solid"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.commissionType !== cur.commissionType}>
                          {({ getFieldValue: gfv2 }) => gfv2('commissionType') === 'RATE' ? (
                            <Form.Item name="commissionRate" label="佣金比例" tooltip="0.05 = 5%，按条目小计 price×quantity×比例 结算"
                              rules={[{ required: true, message: '请输入佣金比例' }]}>
                              <InputNumber min={0.0001} max={1} step={0.01} precision={4} placeholder="如 0.05" style={{ width: '100%' }} />
                            </Form.Item>
                          ) : (
                            <Form.Item name="commissionAmount" label="单件佣金（元）" tooltip="按 commissionAmount×quantity 结算"
                              rules={[{ required: true, message: '请输入单件佣金' }]}>
                              <InputNumber min={0.01} precision={2} placeholder="如 3.00" style={{ width: '100%' }} />
                            </Form.Item>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  ) : null}
                </Form.Item>
              </div>
            ) : null}
          </Form.Item>

          {/* 规格配置 */}
          <div style={sectionStyle}>
            <SectionTitle title="规格配置" desc="商城商品按规格下单，必须至少配置一个规格；积分商品无需规格" />
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.productKind !== cur.productKind}>
              {({ getFieldValue }) => getFieldValue('productKind') === 'POINTS' ? (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 8, color: '#6D28D9', fontSize: 13 }}>
                  积分商品不走现金下单，无需配置规格，保存时会自动清空下方规格。
                </div>
              ) : null}
            </Form.Item>
            <Form.List name="skus" rules={[{
              validator: async (_, skus) => {
                if (form.getFieldValue('productKind') !== 'POINTS' && (!skus || skus.length === 0)) {
                  return Promise.reject(new Error('商城商品必须至少配置一个规格'));
                }
              },
            }]}>
              {(fields, { add, remove }, { errors }) => (
                <>
                  <Row gutter={12}>
                    <Col span={7}><span style={skuTh}>规格名称</span></Col>
                    <Col span={4}><span style={skuTh}>售价（元）</span></Col>
                    <Col span={4}><span style={skuTh} title="高于售价时小程序展示划线价和折扣">划线价</span></Col>
                    <Col span={4}><span style={skuTh}>库存</span></Col>
                    <Col span={4}><span style={skuTh} title="选填；点「图集」为该规格配一组专属图（第一张为封面）。详情页选中该规格时顶部整组切换，不配则沿用商品公共图集">规格图集</span></Col>
                    <Col span={1} />
                  </Row>
                  {fields.map((field) => (
                    <Row gutter={12} key={field.key} align="top">
                      <Col span={7}>
                        <Form.Item name={[field.name, 'id']} hidden><Input /></Form.Item>
                        <Form.Item name={[field.name, 'spec']} style={{ marginBottom: 12 }}
                          rules={[{ required: true, message: '请输入规格名称' }]}>
                          <Input placeholder="如：10片装" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name={[field.name, 'price']} style={{ marginBottom: 12 }}
                          rules={[{ required: true, message: '请输入售价' }]}>
                          <InputNumber min={0} precision={2} placeholder="售价" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name={[field.name, 'originalPrice']} style={{ marginBottom: 12 }}
                          tooltip="高于售价时展示划线价">
                          <InputNumber min={0} precision={2} placeholder="选填" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name={[field.name, 'stock']} initialValue={0} style={{ marginBottom: 12 }}>
                          <InputNumber min={0} placeholder="库存" style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name={[field.name, 'images']} style={{ marginBottom: 12 }}>
                          <SkuImagesEditor />
                        </Form.Item>
                      </Col>
                      <Col span={1} style={{ textAlign: 'center', paddingTop: 8 }}>
                        {fields.length > 1 && (
                          <DeleteOutlined onClick={() => remove(field.name)} style={{ color: '#ef4444', cursor: 'pointer' }} />
                        )}
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" onClick={() => add({ stock: 0 })} icon={<PlusOutlined />} block>
                    添加规格
                  </Button>
                  <Form.ErrorList errors={errors} />
                </>
              )}
            </Form.List>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default Products;
